import React, { FC, ReactNode, useEffect, useId, useCallback, useRef } from "react";
import styles from "./Modal.module.css";
import { Button } from "../../controls/Button/Button";
import { pushModal } from "../../../lib/modalStack";

interface SubmitButtonConfig {
  /**
   * Click handler for a modal that has no `<form>`. Ignored when `formId` is given — the button is
   * then a real submit button and the form's own `onSubmit` is the single entry point.
   */
  onSubmit?: () => void;
  /**
   * `id` of a `<form>` rendered inside `children`.
   *
   * The footer is a **sibling** of the body, so a submit button placed here is outside the form and
   * would do nothing. The `form` attribute is what re-associates the two, and it is the whole reason
   * Enter works: a form with a submit button — wherever that button physically sits — submits on
   * Enter in any of its single-line fields. Textareas and `type="button"` children are exempt by
   * spec, so `Beschreibung` still takes newlines and "Datei durchsuchen" still opens the picker.
   */
  formId?: string;
  title: string;
  isPending?: boolean;
  disabled?: boolean;
}

type PopUpSize = "wide" | "big" | "normal";

/**
 * `center` is the dialog everything else in the app uses: middle of the screen, dimmed backdrop,
 * nothing behind it reachable.
 *
 * `side` docks the panel to the right edge and takes the backdrop away entirely — the page behind
 * stays visible **and interactive**. It exists for the disposition board, where the form and the
 * block it is editing have to be on screen together and a drag on the board writes back into the
 * form. A docked panel is therefore not `aria-modal`, and it does not close on an outside click:
 * every click out there belongs to the page, not to "dismiss".
 */
type PopUpPlacement = "center" | "side";

interface ModalProps {
  children: ReactNode;

  /** Controlled open state */
  show: boolean;
  setShow: (show: boolean) => void;

  /** Optional header */
  title?: string;
  /** One line of context under the title — what this dialog is operating on. */
  subtitle?: ReactNode;
  topLeftChildren?: ReactNode;

  /** Optional primary action button */
  submitButton?: SubmitButtonConfig;

  /** Additional action buttons rendered before the primary button */
  actionButtons?: ReactNode;

  /**
   * A message shown in the footer, above the buttons — for the error the submit attempt produced.
   *
   * It belongs here rather than at the end of the form: the body scrolls, and on a long form the
   * user who just pressed the button at the bottom of the dialog would never see a message appended
   * to content that scrolled away above it.
   */
  footerMessage?: ReactNode;

  /** Visual */
  size?: PopUpSize;
  /** Where the panel sits, and whether the page behind it stays usable. Defaults to `center`. */
  placement?: PopUpPlacement;
  className?: string;
  buttonsClassName?: string;

  /**
   * Auto close after N seconds.
   * - undefined / 0 => disabled
   * - -1 => never auto close (explicit)
   */
  disappear?: number;

  /** If true: render only content without header/footer/cross */
  empty?: boolean;

  /**
   * Callbacks
   * - onClose: fires when modal actually closes
   * - onCloseButtonClick: legacy compatibility (called on any close)
   */
  onClose?: () => void;
  onCloseButtonClick?: () => void;

  /** Accessibility */
  ariaLabel?: string;
}

const Modal: FC<ModalProps> = ({
  children,
  title,
  subtitle,
  show,
  setShow,
  topLeftChildren,

  submitButton,
  actionButtons,
  footerMessage,

  size = "normal",
  placement = "center",
  disappear,
  empty = false,

  className,
  buttonsClassName,

  onClose,
  onCloseButtonClick,

  ariaLabel,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setShow(false);
    onClose?.();
    onCloseButtonClick?.();
  }, [setShow, onClose, onCloseButtonClick]);

  const docked = placement === "side";

  // Auto-close
  useEffect(() => {
    if (!show) return;
    if (!disappear || disappear === 0 || disappear === -1) return;

    const timer = window.setTimeout(() => {
      close();
    }, disappear * 1000);

    return () => window.clearTimeout(timer);
  }, [show, disappear, close]);

  /**
   * Escape, and Tab containment — both owned by `modalStack`, which addresses the topmost showing
   * modal and nothing under it. See the module for why the per-instance `window` listener this
   * replaces was wrong.
   *
   * `close` is read through a ref so a new identity for it — `setShow` is an inline arrow at most
   * call sites — does not pop and re-push the entry, which would reorder the stack under a dialog
   * layered on top.
   */
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  useEffect(() => {
    if (!show) return;
    return pushModal({
      onEscape: () => closeRef.current(),
      panel: () => panelRef.current,
      trapFocus: !docked,
    });
  }, [show, docked]);

  /**
   * Focus goes into the dialog on open and back to whatever opened it on close.
   *
   * The **panel** takes focus, not the first field: these are wide forms whose first control is a
   * `datetime-local` or a search combobox, and focusing either pops a picker or a dropdown over a
   * form the user has not read yet. From the panel, one Tab reaches the first field.
   *
   * Restore is conditional. By the time a modal closes the focus may legitimately have moved on —
   * `onSuccess` navigates, and the docked panel leaves the board clickable throughout — and pulling
   * it back from there would be the modal stealing focus rather than returning it.
   */
  useEffect(() => {
    if (!show) return;

    const previous = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    panel?.focus({ preventScroll: true });

    return () => {
      const active = document.activeElement;
      const focusIsStillOurs =
        active === document.body || (panel != null && panel.contains(active));
      if (!focusIsStillOurs) return;
      if (previous && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [show]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close();
  };

  // Keep markup mounted to preserve animations if CSS relies on it
  return (
    <div
      className={[
        styles.popUpContainer,
        docked ? styles.docked : "",
        show ? styles.show : "",
      ].join(" ")}
      // Docked, the area around the panel is the page — clicking it must reach what is under it,
      // not dismiss the form the user is filling in.
      onClick={docked ? undefined : handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        // Focus target on open. Not a tab stop — `-1` keeps it out of the Tab cycle while letting
        // it be focused programmatically.
        tabIndex={-1}
        className={[
          styles.popUp,
          docked ? styles.sidePanel : "",
          size === "big" ? styles.big : "",
          size === "wide" ? styles.wide : "",
          className ?? "",
        ].join(" ")}
        role="dialog"
        // A panel that leaves the rest of the page reachable is not modal, and saying so would tell
        // a screen reader to hide everything the user can still click.
        aria-modal={!docked}
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
      >
        {empty ? (
          children
        ) : (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerText}>
                {title && (
                  <div id={titleId} className={styles.title}>
                    {title}
                  </div>
                )}
                {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
              </div>
              {/* Named for where it used to sit. With the title left-aligned it trails the title
                  row instead, the way a card's `action` does. */}
              {topLeftChildren && <div className={styles.headerAction}>{topLeftChildren}</div>}
            </div>

            {/* Body */}
            <div className={`${styles.body} ${styles.bodyScrollable}`}>{children}</div>

            {/* Footer */}
            <div className={`${styles.footer} ${buttonsClassName ?? ""}`}>
              {footerMessage}
              {actionButtons}
              {submitButton && (
                <Button
                  // `form` reaches across the DOM to the form in the body; see `SubmitButtonConfig`.
                  // Without it the button is what it always was — an onClick on a `type="button"`.
                  type={submitButton.formId ? "submit" : "button"}
                  form={submitButton.formId}
                  onClick={submitButton.formId ? undefined : submitButton.onSubmit}
                  isLoading={submitButton.isPending}
                  disabled={submitButton.disabled}
                  fullWidth
                  className={styles.button}
                >
                  {submitButton.title}
                </Button>
              )}
            </div>

            {/* Close (X) */}
            <button
              type="button"
              onClick={close}
              className={styles.cross}
              aria-label="Close"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path
                  d="M4 4L16 16M16 4L4 16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;